import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { useTranslation } from 'react-i18next';
import { ProcessSummary } from './processSumary';
import { DarwinFlex, DarwinListView, formatDate } from 'components/darwin-ui';
import DarwinTypography from 'components/darwin-ui/Typography';
import styles from './ErrorDetails.module.css';
import { useLocation } from 'react-router-dom';
import { capitalize } from 'utils/common';
import { generatePath, useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/routes';
import { useGetMassiveLoadErrorDetails } from 'api/services/dataCaptureService';
import { useEffect, useMemo } from 'react';
import { getErrorDetailsColumns } from './columns';
import {
  MassiveDetailsType,
  MassiveTransaction,
  MassiveTransactionErrorResponse,
} from 'types/massiveTransactionTypes';

const isEmptyData = (data?: MassiveDetailsType): boolean =>
  !data || (!data.uploadDate && !data.endDate && !data.process);

export const ErrorDetails = () => {
  const { t } = useTranslation();
  const { state } = useLocation() as { state: MassiveTransaction | undefined };
  const navigate = useNavigate();

  const { data: errorDetails, isLoading: isLoadingMassiveErrorDetail } =
    useGetMassiveLoadErrorDetails({
      massiveLoadId: state?.id,
    });

  const processData = useMemo<MassiveDetailsType>(() => {
    return {
      uploadDate:
        formatDate(state?.createdAt ?? '', 'dd/MM/yyyy, HH:mm:ss') ?? undefined,
      endDate:
        formatDate(state?.completedAt ?? '', 'dd/MM/yyyy, HH:mm:ss') ??
        undefined,
      process: capitalize(state?.processName ?? '') ?? undefined,
      formName: state?.formName ?? undefined,
      processMapName: state?.processMapName ?? undefined,
      fileName: state?.metadata?.filename ?? undefined,
    };
  }, [
    state?.createdAt,
    state?.completedAt,
    state?.processName,
    state?.formName,
    state?.processMapName,
    state?.metadata?.filename,
  ]);

  const status = useMemo(() => {
    const processing = state?.processingStatus?.toLowerCase();

    if (processing === 'failed') return 'error';
    if (processing === 'completed_success') return 'success';
    if (processing === 'completed_with_errors') return 'warning';

    return 'undefined';
  }, [state?.processingStatus]);

  const statusText = useMemo(() => {
    if (status === 'error') return t('error-details.status.error');
    if (status === 'success') return t('error-details.status.success');
    if (status === 'warning') return t('error-details.status.warning');
    return '';
  }, [status, t]);

  const columns = useMemo(() => getErrorDetailsColumns(t), [t]);

  const tableData = useMemo(() => errorDetails?.data ?? [], [errorDetails]);

  const isEmpty = !errorDetails?.data || errorDetails.data.length === 0;

  const returnUploadView = () => {
    navigate(generatePath(ROUTES.UPLOAD_HISTORY));
  };

  useEffect(() => {
    if (!state || isEmptyData(processData)) {
      navigate(generatePath(ROUTES.UPLOAD_HISTORY));
    }
  }, [navigate, state, processData]);

  return (
    <>
      <ContentHeader
        title={t('error-details.title')}
        onSave={() => returnUploadView()}
        onSaveTitle={t('error-details.return')}
      />

      <DarwinFlex vertical style={{ marginTop: 20 }}>
        <DarwinFlex
          vertical
          style={{ gap: 10, marginLeft: 20, marginRight: 20 }}
        >
          <DarwinTypography.Text className={styles.titleComponent}>
            {t('error-details.subtitle')}
          </DarwinTypography.Text>

          {status === 'error' ? (
            <DarwinTypography.Text className={styles.regularTextComponent}>
              {t('error-details.content-error')}
            </DarwinTypography.Text>
          ) : status === 'warning' ? (
            <DarwinTypography.Text className={styles.regularTextComponent}>
              {t('error-details.content-warning')}
            </DarwinTypography.Text>
          ) : (
            <DarwinTypography.Text className={styles.regularTextComponent}>
              {t('error-details.content-success')}
            </DarwinTypography.Text>
          )}

          <DarwinFlex>
            <ProcessSummary
              data={processData}
              status={{ label: statusText, type: status }}
            />
          </DarwinFlex>
        </DarwinFlex>

        <DarwinListView<MassiveTransactionErrorResponse>
          data={tableData}
          columns={columns}
          isLoading={isLoadingMassiveErrorDetail}
          emptyText={isEmpty ? t('error-details.no-errors') : undefined}
        />
      </DarwinFlex>
    </>
  );
};
